/** Orders list — DataTable with filters. */

import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/shared/DataTable";
import { api } from "@/lib/api";
import {
  PAYMENT_STATUS_LABELS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  type OrderStatus,
} from "@/lib/order-status";
import { formatDate, formatPrice } from "@/lib/utils";

interface OrderRow {
  id: number;
  public_id: string;
  order_number: string;
  status: OrderStatus;
  payment_status: string;
  payment_provider: string | null;
  contact_name: string;
  contact_phone: string;
  items_count: number;
  grand_total: number;
  created_at: string;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Все статусы" },
  ...(Object.entries(STATUS_LABELS).map(([v, label]) => ({ value: v, label }))),
];

export function OrdersListPage(): JSX.Element {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders", page, perPage, q, statusFilter],
    queryFn: async () => {
      const params: Record<string, any> = { page, per_page: perPage };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/orders", { params });
      return data;
    },
  });

  const columns: ColumnDef<OrderRow>[] = [
    {
      accessorKey: "order_number",
      header: "Номер",
      cell: ({ row }) => (
        <Link
          to={`/orders/${row.original.public_id}`}
          className="font-mono text-sm hover:underline"
        >
          {row.original.order_number}
        </Link>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Дата",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.created_at)}
        </span>
      ),
    },
    {
      accessorKey: "contact_name",
      header: "Клиент",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.contact_name}</div>
          <a
            href={`tel:${row.original.contact_phone}`}
            className="text-xs text-muted-foreground hover:underline"
          >
            {row.original.contact_phone}
          </a>
        </div>
      ),
    },
    {
      accessorKey: "items_count",
      header: "Поз.",
      cell: ({ row }) => (
        <span className="font-mono">{row.original.items_count}</span>
      ),
    },
    {
      accessorKey: "grand_total",
      header: "Сумма",
      cell: ({ row }) => (
        <span className="font-medium">{formatPrice(row.original.grand_total)}</span>
      ),
    },
    {
      accessorKey: "payment_status",
      header: "Оплата",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.payment_status === "paid"
              ? "success"
              : row.original.payment_status === "pending"
                ? "warning"
                : "secondary"
          }
        >
          {PAYMENT_STATUS_LABELS[row.original.payment_status] ?? row.original.payment_status}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: "Статус",
      cell: ({ row }) => (
        <Badge variant={STATUS_VARIANTS[row.original.status]}>
          {STATUS_LABELS[row.original.status]}
        </Badge>
      ),
    },
  ];

  function exportCsv() {
    const url = `${import.meta.env.VITE_API_URL}/orders/export/csv`;
    window.open(url, "_blank");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">{t("nav.orders")}</h1>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="w-4 h-4 mr-1" />
          Экспорт CSV
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Номер, ФИО или телефон"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.pagination?.total ?? 0}
        page={page}
        pageSize={perPage}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
}
